import { ArrowLeftOutlined } from '@ant-design/icons';
import commaNumber from 'comma-number';
import { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import ExpandSearchInput from '@/components/Inputs/ExpandSearchInput';
import { useSearch } from '@/components/Inputs/useSearch';
import Modal from '@/components/Modal/Modal';
import Typo from '@/components/Typography/Typo';
import AMP_KEYS from '@/configs/amp-track-keys';
import TaxonomyContext from '@/features/Taxonomy/context/TaxonomyContext';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useModalState from '@/hooks/pure/useModalState';
import useTaxonomyStore from '@/stores/TaxonomyStore';
import { customScrollbar } from '@/styles/style';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';
import keymap from '@/utils/keymap';
import { track } from '@/utils/track/track';
import { lowerCaseMatch } from '@/utils/util';

import {
  buildTaxonomyFeedbackPath,
  buildTaxonomyListPath,
} from '../../../utils/routes';
import AISuggestionModal from '../../TaxonomyDetail/AISuggestionModal/AISuggestionModal';
import SelectTopicTypeModal from '../../TaxonomyDetail/CreateTopicModal/SelectTopicTypeModal';
import type { CreateTopicModalProps } from '../../TaxonomyDetail/CreateTopicModal/types';
import TaxonomyRow from '../TaxonomyRow';
import TopicNavigator from '../TopicNavigator/TopicNavigator';

type SidebarProps = {
  taxonomyAlias: string;
  topicAlias?: string;
};

const Topic2DetailSidebar = (props: SidebarProps) => {
  const wr = useWorkspaceRouter();
  const { fetchTopics, taxonomy, taxonomyAlias, refetchTopicDetail } =
    useContext(TaxonomyContext);
  if (!taxonomyAlias) throw new Error('TaxonomyAlias not found');
  const topicTreeMap = useTaxonomyStore((state) => state.topicTreeMap);

  const {
    onKeyEnterSearch: onKeyEnterTopicSearch,
    searchValue: topicSearchValue,
    initSearchValue: initTopicSearchValue,
  } = useSearch();

  const {
    status: createModalStatus,
    modalState: createModalState,
    setModalState: setCreateModalState,
    open: openCreateModal,
    close: closeCreateModal,
  } = useModalState<CreateTopicModalProps<CreateTopicTarget> | null>(null);

  const {
    status: aiSuggestionModalStatus,
    modalState: aiSuggestionModalState,
    setModalState: setAISuggestionModalState,
    open: openAiSuggestionModal,
    close: closeAiSuggestionModal,
  } = useModalState<CreateTopicModalProps<CreateTopicTarget> | null>(null);

  const currTaxonomy = useMemo(() => {
    const taxonomyTopicMap = topicTreeMap[props.taxonomyAlias];
    if (!taxonomyTopicMap)
      return {
        taxonomy: {
          alias: taxonomy?.alias || '',
          title: taxonomy?.title || '',
        },
        root: [],
        topicMap: {},
      };
    return taxonomyTopicMap;
  }, [props.taxonomyAlias, taxonomy?.alias, taxonomy?.title, topicTreeMap]);

  const currTaxonomyRootTopics = useMemo(() => {
    return currTaxonomy.root.map((alias) => currTaxonomy.topicMap[alias]);
  }, [currTaxonomy.root, currTaxonomy.topicMap]);

  const filteredRootTopics = useMemo(() => {
    if (topicSearchValue.trim() === '') return currTaxonomyRootTopics;
    const taxonomyTopicMap = currTaxonomy?.topicMap;
    if (!taxonomyTopicMap) return [];
    const filteredTopicArr = Object.values(taxonomyTopicMap)
      .filter(
        (topic) =>
          lowerCaseMatch(topic.name, topicSearchValue),
      )
      .map((topic) => {
        return {
          ...topic,
          children: [],
          indent: 1,
        };
      });
    return filteredTopicArr;
  }, [currTaxonomy?.topicMap, currTaxonomyRootTopics, topicSearchValue]);

  const visibleTopicCount = useMemo(() => {
    return Object.values(currTaxonomy.topicMap).length;
  }, [currTaxonomy.topicMap]);

  const rootTopicType: TOPIC_2_TYPE | null = useMemo(() => {
    if (currTaxonomyRootTopics.length > 0) {
      return currTaxonomyRootTopics[0].category;
    }
    return null;
  }, [currTaxonomyRootTopics]);

  useEffect(() => {
    fetchTopics();
  }, []);

  const onCreateTopicClick = () => {
    setCreateModalState({
      target: 'taxonomy',
      targetAlias: taxonomyAlias,
      taxonomyAlias: taxonomyAlias,
      type: rootTopicType,
      targetName: taxonomy?.title || '',
    });
    openCreateModal();
  };

  const taxonomyCreateOptions = useMemo(() => {
    return [
      {
        label: 'Explore with AI',
        onClick: () => {
          openAiSuggestionModal();
          setAISuggestionModalState({
            type: rootTopicType,
            target: 'taxonomy',
            targetAlias: taxonomyAlias,
            taxonomyAlias: taxonomyAlias,
            targetName: taxonomy?.title || '',
          });
        },
        disabled:
          rootTopicType === TOPIC_2_TYPE.MANUAL ||
          rootTopicType === TOPIC_2_TYPE.RULE,
      },
      {
        label: 'Create your own',
        onClick: () => {
          setCreateModalState({
            type: rootTopicType,
            target: 'taxonomy',
            targetAlias: taxonomyAlias,
            taxonomyAlias: taxonomyAlias,
            targetName: taxonomy?.title || '',
          });
          openCreateModal();
        },
      },
    ];
  }, [
    openAiSuggestionModal,
    openCreateModal,
    rootTopicType,
    setAISuggestionModalState,
    setCreateModalState,
    taxonomy?.title,
    taxonomyAlias,
  ]);

  return (
    <>
      <VFlex padding="32px 32px 16px 32px" g={24}>
        <Flex a="center" g={12}>
          <IconButton
            icon={
              <ArrowLeftOutlined
                style={{
                  fontSize: '17px',
                }}
              />
            }
            onClick={() => wr.push(buildTaxonomyListPath())}
            sizeType="m"
            outlined={false}
          />
          <Typo type="label0" weight="sb">
            Taxonomy
          </Typo>
        </Flex>
        <VFlex g={12}>
          <Flex g={12} j="space-between" a="center">
            <Typo type="label2" weight="m">
              Topic ({commaNumber(visibleTopicCount)})
            </Typo>
            <Flex f={1} j="flex-end">
              <ExpandSearchInput
                shrink
                sizeType="l"
                placeholder="Search topic"
                onClearInput={initTopicSearchValue}
                onKeyDown={(e) => {
                  if (keymap.isEnter(e)) {
                    track(AMP_KEYS.TAXONOMY_TOPIC_SEARCH);
                    onKeyEnterTopicSearch(e);
                  }
                }}
              />
            </Flex>
          </Flex>
        </VFlex>
      </VFlex>

      <NavigatorVFlex>
        {taxonomy && (
          <TaxonomyRow
            alias={taxonomy?.alias}
            name={taxonomy?.title || ''}
            onClick={() => {
              wr.push(buildTaxonomyFeedbackPath(props.taxonomyAlias));
            }}
            focused={!props.topicAlias}
            onCreateTopicClick={onCreateTopicClick}
            createOptions={taxonomyCreateOptions}
          />
        )}

        {currTaxonomyRootTopics.length > 0 && (
          <TopicNavigator
            isSearchResult={!!topicSearchValue.trim()}
            topicData={filteredRootTopics}
            currentTopic={props.topicAlias}
            createTopicCallback={(alias: string) => {
              fetchTopics();
              refetchTopicDetail(alias);
            }}
          />
        )}

        <Modal
          status={createModalStatus}
          closeModal={() => {
            closeCreateModal();
            fetchTopics();
          }}
        >
          {createModalState && <SelectTopicTypeModal {...createModalState} />}
        </Modal>
        <Modal
          status={aiSuggestionModalStatus}
          closeModal={() => {
            closeAiSuggestionModal();
            fetchTopics();
          }}
        >
          {aiSuggestionModalState && (
            <AISuggestionModal
              target={aiSuggestionModalState.target}
              targetAlias={aiSuggestionModalState.targetAlias}
              taxonomyAlias={aiSuggestionModalState.taxonomyAlias}
              targetName={aiSuggestionModalState.targetName}
            />
          )}
        </Modal>
      </NavigatorVFlex>
    </>
  );
};

const NavigatorVFlex = styled(VFlex)`
  padding: 32px;
  padding-top: 0px;
  overflow: auto;
  ${customScrollbar};
`;

export default Topic2DetailSidebar;
